import { useCurrentUser } from '../../Contexts/CurrentUserProvider'
import type { WorkflowStatus } from '../../types'
import * as styles from './styles'

export const statusText = {
  approved: 'Final approval has been given',
  approvalNeeded: (approvalCount: number) =>
    `You have ${approvalCount} ${
      approvalCount > 1 ? 'approvals' : 'approval'
    } in queue`,
  assignedTasks: (taskCount: number) =>
    `You have ${taskCount} ${taskCount > 1 ? 'tasks' : 'task'} in queue`,
  complete: 'No outstanding assignments',
  denied: 'Needs changes before approval',
  deniedByUser: "You've denied approval",
  pause: 'Workflow is paused',
  pendingApproval: 'Currently awaiting review',
  pendingTasks: (taskCount: number) =>
    `${taskCount} ${taskCount > 1 ? 'tasks' : 'task'} in current phase`,
}

const getStatusText = (
  { activeStage, state, approved }: WorkflowStatus,
  currentUserId: string
) => {
  // workflow is paused
  if (activeStage?.stageType === 'circuit_breaker') return statusText.pause
  // All tasks complete
  if (state === 'complete') return statusText.complete

  if (activeStage?.stageType === 'task') {
    // user has assigned tasks in stage
    if (activeStage.assignmentCountForUser)
      return statusText.assignedTasks(activeStage.assignmentCountForUser)

    // open tasks not assigned to user
    return statusText.pendingTasks(activeStage.assignmentCount)
  }

  if (activeStage?.declined) {
    // user has denied approval
    if (activeStage.declined.userId === currentUserId)
      return statusText.deniedByUser

    // another user has denied approval
    return statusText.denied
  }

  // approved by any user
  if (approved) return statusText.approved

  // user has open approvals
  if (activeStage?.assignmentCountForUser)
    return statusText.approvalNeeded(activeStage.assignmentCountForUser)

  // open approvals not assigned to user
  return statusText.pendingApproval
}

export type StatusTextProps = {
  status?: WorkflowStatus
}

export const StatusText: React.FC<StatusTextProps> = ({ status }) => {
  const currentUser = useCurrentUser()

  if (!status || status.state === 'inactive') return null

  return (
    <p css={styles.base}>{getStatusText(status, currentUser?.uuid ?? '')}</p>
  )
}
